import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Row, Col, FormGroup, Label, Button, Spinner, Alert, Input, Collapse } from 'reactstrap';
import Select from 'components/Select';
import { useGetUserPermissions, useUpdateTenantUser, useUpdateSAUser } from '../../api/users/service';
import { useGetRoles } from 'api/roles/service';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveColorStyle } from '../../utils/colorUtils';
import { getSelectStyles } from 'utils/selectStyles';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const SecurityPermissions = React.forwardRef(({
  userDetail,
  id: propId,
  activeColor = "secondary",
  isSA = false,
  tenantId = null,
  onTenantChange = () => {},
  isTheme2 = false,
  inputStyle,
  inputClassName,
  selectStyles: theme2SelectStyles,
}, ref) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const id = propId || params.id;
  const { data: rolesData, isLoading: rolesLoading } = useGetRoles(tenantId);
  const { data: permissionsData, isLoading, isError, error } = useGetUserPermissions();
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [customRoleName, setCustomRoleName] = useState('');
  const [roleNameError, setRoleNameError] = useState('');
  const [openGroups, setOpenGroups] = useState([]); // for accordion functionality
  const updateTenantUserMutation = useUpdateTenantUser();
  const updateSAUserMutation = useUpdateSAUser();
  const [isLightTheme, setIsLightTheme] = useState(false);

  // Theme control
  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = isTheme2 && theme2SelectStyles ? theme2SelectStyles : customSelectStyles;
  const compactBtnStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR, padding: '2px 8px' }
    : { fontSize: '12px', padding: '2px 8px' };
  const toggleIconStyle = isTheme2
    ? { fontSize: `calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 2px)`, fontWeight: 'bold', cursor: 'pointer' }
    : { fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' };

  // Create role options from rolesData
  const roleOptions = rolesData?.results ? [
    { value: 'custom', label: 'Custom' },
    ...rolesData.results.map(role => ({
      value: role.documentId,
      label: role.name
    }))
  ] : [];

  useEffect(() => {
    if (userDetail?.permissions && permissionsData?.data) {
      const userPermissions = userDetail.permissions;
      const selectedIds = [];
      permissionsData.data.forEach((group) => {
        group.permissions.forEach((permission) => {
          if (userPermissions[permission.key] === true) {
            selectedIds.push(permission.id);
          }
        });
      });
      setSelectedPermissions(selectedIds);
    }
  }, [userDetail, permissionsData]);

  // Set selected role based on userDetail.user_role
  useEffect(() => {
    if (userDetail?.user_role && rolesData?.results) {
      // Check if user_role matches any role in rolesData
      const matchingRole = rolesData.results.find(role => role.name === userDetail.user_role);
      if (matchingRole) {
        setSelectedRole(matchingRole.documentId);
      } else {
        // If no matching role found, set to custom
        setSelectedRole('custom');
      }
    }
  }, [userDetail?.user_role, rolesData]);

  const permissionGroups = permissionsData?.data || [];

  // Handle accordion state based on selected role
  useEffect(() => {
    if (selectedRole === 'custom') {
      // Keep all accordions closed for custom mode
      setOpenGroups([]);
    } else {
      // Open all accordions for predefined role mode
      if (permissionGroups.length > 0) {
        setOpenGroups(permissionGroups.map(group => group.id));
      }
    }
  }, [selectedRole, permissionGroups]);

  // Custom accordion toggle logic
  const toggleGroup = (id) => {
    // Only allow toggling in custom mode
    if (selectedRole === 'custom') {
      setOpenGroups((prevOpen) =>
        prevOpen.includes(id)
          ? prevOpen.filter((item) => item !== id)
          : [...prevOpen, id]
      );
    }
  };

  // Select all permissions in a group
  const handleSelectAllInGroup = (group) => {
    // Only allow in custom mode
    if (selectedRole !== 'custom') return;

    const groupPermissionIds = group.permissions.map(permission => permission.id);
    const currentGroupSelected = groupPermissionIds.filter(id => selectedPermissions.includes(id));

    let newSelectedPermissions;
    if (currentGroupSelected.length === groupPermissionIds.length) {
      // If all are selected, deselect all
      newSelectedPermissions = selectedPermissions.filter(id => !groupPermissionIds.includes(id));
    } else {
      // If not all are selected, select all
      newSelectedPermissions = [...selectedPermissions];
      groupPermissionIds.forEach(id => {
        if (!newSelectedPermissions.includes(id)) {
          newSelectedPermissions.push(id);
        }
      });
    }

    setSelectedPermissions(newSelectedPermissions);
  };

  // Check if all permissions in a group are selected
  const isAllSelectedInGroup = (group) => {
    const groupPermissionIds = group.permissions.map(permission => permission.id);
    return groupPermissionIds.every(id => selectedPermissions.includes(id));
  };

  const handlePermissionChange = (permissionId, checked) => {
    // Only allow permission changes if custom role is selected
    if (selectedRole === 'custom') {
      if (checked) {
        setSelectedPermissions((prev) => [...prev, permissionId]);
      } else {
        setSelectedPermissions((prev) => prev.filter((id) => id !== permissionId));
      }
    }
  };

  const handleRoleChange = (roleId) => {
    setSelectedRole(roleId);
    setRoleNameError(''); // Clear any previous errors

    if (roleId === 'custom') {
      // Custom role - keep current selections
      setSelectedPermissions(selectedPermissions);
      setCustomRoleName(''); // Reset custom role name
    } else {
      setCustomRoleName(''); // Reset custom role name when not custom
      // Find the selected role from rolesData
      const selectedRoleData = rolesData?.results?.find(role => role.documentId === roleId);

      if (selectedRoleData && selectedRoleData.permissions) {
        // Set permissions based on the selected role's permissions
        const rolePermissions = [];
        permissionGroups.forEach((group) => {
          group.permissions.forEach((permission) => {
            if (selectedRoleData.permissions[permission.key] === true) {
              rolePermissions.push(permission.id);
            }
          });
        });
        setSelectedPermissions(rolePermissions);
      } else {
        setSelectedPermissions([]);
      }
    }
  };

  // Handle initial role selection when userDetail.user_role is available
  useEffect(() => {
    if (selectedRole && selectedRole !== 'custom' && rolesData?.results && permissionGroups.length > 0) {
      const selectedRoleData = rolesData.results.find(role => role.documentId === selectedRole);

      if (selectedRoleData && selectedRoleData.permissions) {
        // Set permissions based on the selected role's permissions
        const rolePermissions = [];
        permissionGroups.forEach((group) => {
          group.permissions.forEach((permission) => {
            if (selectedRoleData.permissions[permission.key] === true) {
              rolePermissions.push(permission.id);
            }
          });
        });
        setSelectedPermissions(rolePermissions);
      }
    }
  }, [selectedRole, rolesData, permissionGroups]);


  const handleSave = async () => {
    // Validate custom role name if custom role is selected
    if (selectedRole === 'custom') {
      if (!customRoleName || customRoleName.trim() === '') {
        setRoleNameError(t('userDetail.security.roleNameRequired') || 'Role name is required');
        return;
      }
      setRoleNameError('');
    }

    // Format permissions in the same structure as userDetail.permissions
    const formattedPermissions = {};

    permissionGroups.forEach((group) => {
      group.permissions.forEach((permission) => {
        // Using just permission.key to match the userDetail.permissions format
        formattedPermissions[permission.key] = selectedPermissions.includes(permission.id);
      });
    });


    const updateUserId = isSA ? userDetail?.id : id;

    // If a predefined role is selected, also log the role details
    if (selectedRole !== 'custom') {
      const selectedRoleData = rolesData?.results?.find(role => role.documentId === selectedRole);
      const updatePayload = {
        id: updateUserId,
        data: {
          user: {
            user_roles: selectedRoleData.id
          }
        }
      };

      if (isSA && tenantId) {
        await updateSAUserMutation.mutateAsync({ ...updatePayload, tenantId });
      } else {
        await updateTenantUserMutation.mutateAsync(updatePayload);
      }
    } else {
      const updatePayload = {
        id: updateUserId,
        data: {
          new_role: {
            name: customRoleName,
            permissions: formattedPermissions
          }
        }
      };

      if (isSA && tenantId) {
        await updateSAUserMutation.mutateAsync({ ...updatePayload, tenantId });
      } else {
        await updateTenantUserMutation.mutateAsync(updatePayload);
      }
    }
  };

  React.useImperativeHandle(ref, () => ({
    handleSave
  }));

  if (isLoading || rolesLoading) {
    return <div className="text-center my-4"><Spinner color="primary" /> {t('userDetail.security.loading')}</div>;
  }
  if (isError) {
    return <Alert color="danger">{t('userDetail.security.error', { message: error?.message || '' })}</Alert>;
  }

  return (
    <Card>
      <CardHeader>
        <b>{t('userDetail.security.title')}</b>
      </CardHeader>
      <CardBody>
        <Row className="mb-3">
          {isSA && (
            <Col md="6">
              <FormGroup>
                <Label>Select Tenant for Permissions</Label>
                <Select
                  styles={selectStyles}
                  className="react-select"
                  placeholder="Select Tenant"
                  isSearchable
                  value={
                    (userDetail?.user?.tenants || userDetail?.tenants || []).find(t => t.id == tenantId)
                      ? { value: tenantId, label: (userDetail?.user?.tenants || userDetail?.tenants || []).find(t => t.id == tenantId).name }
                      : null
                  }
                  onChange={(option) => onTenantChange(option?.value || null)}
                  options={(userDetail?.user?.tenants || userDetail?.tenants || []).map(t => ({
                    value: t.id,
                    label: t.name
                  }))}
                />
              </FormGroup>
            </Col>
          )}
          <Col md="6">
            <FormGroup>
              <Label>{t('userDetail.security.userRole')}</Label>
              <Select
                styles={selectStyles}
                className="react-select"
                placeholder={t('userDetail.security.selectRole')}
                isSearchable
                closeMenuOnSelect={true}
                value={selectedRole ? roleOptions.find(opt => opt.value === selectedRole) : null}
                onChange={(option) => handleRoleChange(option?.value || '')}
                options={roleOptions}
                isLoading={rolesLoading}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Custom Role Name Input - Only show when custom is selected */}
        {selectedRole === 'custom' && (
          <Row className="mb-3">
            <Col md="6">
              <FormGroup>
                <Label>
                  Role Name <span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter custom role name"
                  value={customRoleName}
                  onChange={(e) => {
                    setCustomRoleName(e.target.value);
                    if (roleNameError) setRoleNameError('');
                  }}
                  className={
                    isTheme2 && inputClassName
                      ? inputClassName(roleNameError)
                      : roleNameError
                        ? "is-invalid"
                        : undefined
                  }
                  style={isTheme2 ? inputStyle : undefined}
                />
                {roleNameError && (
                  <div className="invalid-feedback d-block">
                    {roleNameError}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
        )}

        <hr />
        {selectedRole && selectedRole !== 'custom' && (
          <Alert color="info" className="mb-3">
            <i className="tim-icons icon-lock-circle mr-2"></i>
            {t('userDetail.security.lockedInfo')}
          </Alert>
        )}
        {permissionGroups.map((group) => (
          <Card
            key={group.id}
            className="mb-2"
            style={{
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              transition: "box-shadow 0.3s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (selectedRole === 'custom') {
                e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <CardHeader
              onClick={() => toggleGroup(group.id)}
              style={{
                cursor: selectedRole === 'custom' ? 'pointer' : 'default',
                userSelect: 'none',
                padding: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}
            >
              <div>
                <b>{group.name.charAt(0).toUpperCase() + group.name.slice(1)}</b>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Button
                  size="sm"
                  color="primary"
                  outline
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAllInGroup(group);
                  }}
                  style={compactBtnStyle}
                  disabled={selectedRole !== 'custom'}
                >
                  {isAllSelectedInGroup(group) ? t('userDetail.security.deselectAll') : t('userDetail.security.selectAll')}
                </Button>
                {selectedRole === 'custom' && (
                  <span style={toggleIconStyle}>
                    {openGroups.includes(group.id) ? '-' : '+'}
                  </span>
                )}
              </div>
            </CardHeader>
            <Collapse isOpen={openGroups.includes(group.id)}>
              <CardBody>
                <Row>
                  {group.permissions.map((permission) => (
                    <Col md="4" key={permission.id} className="mb-2">
                      <FormGroup check className="mt-3">
                        <Label
                          check
                          htmlFor={`perm_${permission.id}`}
                          style={{
                            opacity: selectedRole !== 'custom' ? 0.7 : 1,
                            cursor: selectedRole !== 'custom' ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <Input
                            type="checkbox"
                            id={`perm_${permission.id}`}
                            className="form-check-input"
                            checked={selectedPermissions.includes(permission.id)}
                            onChange={e => handlePermissionChange(permission.id, e.target.checked)}
                            disabled={selectedRole !== "custom"}
                          />
                          <span className="form-check-sign">
                            <span className="check" />
                          </span>
                          {permission.name}
                        </Label>
                      </FormGroup>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        ))}
        {/* Bottom buttons removed as they are now in UserDetail.js header */}
      </CardBody>
    </Card>
  );
});

export default SecurityPermissions; 